import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppMenuCheck } from "./AppMenuCheck";

// AppDemo - DEMO MODE (owner, 2026-10-26: "something I switch to from my regular log in. It just hides my data and
// replaces it with the demo content"). A switch in the settings menu, kept in this browser (`localStorage` "wac.demo",
// "1" = on). While on, the page reads the service's INVENTED archive (`AppDemoSource` -> `/demo`) instead of the
// subscriber's Dropbox, sends nothing anywhere (`AppDemoSource.Queue`), starts no live feed of the real account, and
// AI questions carry `demo: true` so the service answers them about the invented chats with the owner's own model.
// ⭐ Turning it on or off RELOADS the page: nothing of the real archive is left in memory or on screen.
// ⚠ Reaches `localStorage` / `document` / `location` through `globalThis` (the Bridge typecheck has no DOM lib).

interface IDemoStore { getItem(K: string): string | null; setItem(K: string, V: string): void; removeItem(K: string): void; }
interface IDemoNode { closest?(S: string): IDemoNode | null; }
interface IDemoBar { insertAdjacentHTML(Where: string, Html: string): void; }
interface IDemoXhr { open(M: string, U: string, A: boolean): void; setRequestHeader(N: string, V: string): void; send(B: string): void; }
interface IDemoDoc { getElementById(Id: string): unknown | null; querySelector(S: string): IDemoBar | null; }

export class AppDemo {
    public static readonly Key: string = "wac.demo";
    public static readonly Attr: string = "data-wac-demo";
    public static readonly Words: string = "Demo mode";
    public static readonly Tip: string = "Hides your chats and shows an invented archive, for showing WACommander to others. Nothing is sent from it.";
    /** The contacts' language set (`localStorage` "wac.demo.lang"): "" all, or en / sv / id - the service filters the list. */
    public static readonly LangKey: string = "wac.demo.lang";
    public static readonly Langs: string[][] = [["", "All contacts"], ["en", "English contacts"], ["sv", "Swedish contacts"], ["id", "Indonesian contacts"]];
    /** The example warnings the menu can show (`DemoLive.Warnings` on the service). */
    public static readonly Warnings: string[][] = [["none", "No warning"], ["limited", "Account limited"], ["temporarily-banned", "Temporarily blocked"],
        ["banned", "Banned"], ["relink", "Link lost - re-link"]];
    public static readonly WarnKey: string = "wac.demo.warn";
    /** The service, for the warning toggle (`UseService` keeps it). */
    public static ServiceUrl: string = "";

    /** Is demo mode on? Storage that cannot be read = off. */
    public static On = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Store: IDemoStore | undefined = (globalThis as unknown as { localStorage?: IDemoStore }).localStorage;
            if (Store === undefined) { return false; }
            return Store.getItem(AppDemo.Key) === "1";
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: blocked storage (private mode, policy) throws on access; off is the answer.
            return false;
        }
    };

    /** Store the choice. ERROR-SUPPRESSED-JUSTIFIED: blocked storage = the switch cannot be kept; the reload shows it off. */
    public static Set = (On: boolean): void => {
        try {
            const Store: IDemoStore | undefined = (globalThis as unknown as { localStorage?: IDemoStore }).localStorage;
            if (Store === undefined) { return; }
            if (On === true) { Store.setItem(AppDemo.Key, "1"); return; }
            Store.removeItem(AppDemo.Key);
        } catch (e) {
            return;
        }
    };

    /** The settings menu item (`menuitemcheckbox`). DOCUMENTED EXCEPTION: pure markup. */
    public static Item = (): string => {
        // ERROR-SUPPRESSED-JUSTIFIED: pure markup (`AppMenuCheck.Html`).
        return AppMenuCheck.Html(AppDemo.Attr, "toggle", AppDemo.On(), AppDemo.Tip, AppDemo.Words);
    };

    /**
     * While demo mode is on, the settings menu also holds its two choices (owner 2026-10-26: "Remove the whole band"):
     * which contacts, and an example warning. Leaving demo mode = the switch above them. DOCUMENTED EXCEPTION: pure markup.
     */
    public static MenuPicks = (): string => {
        if (AppDemo.On() !== true) { return ""; }
        return "<div class=\"demopicks\">"
            + AppDemo.Pick("wac-demolang", "Contacts in the demo", AppDemo.Langs, AppDemo.Read(AppDemo.LangKey))
            + AppDemo.Pick("wac-demowarn", "Example warning", AppDemo.Warnings, AppDemo.Read(AppDemo.WarnKey)) + "</div>";
    };

    /** A labelled `<select>` of the band. DOCUMENTED EXCEPTION: pure markup of our own constants. */
    private static Pick = (Id: string, Label: string, Choices: string[][], Now: string): string => {
        let Out: string = "<label class=\"demopick\"><span class=\"sr\">" + Label + "</span><select id=\"" + Id + "\" title=\"" + Label + "\">";
        for (const [Value, Words] of Choices) {
            let Sel: string = "";
            if (Value === Now) { Sel = " selected"; }
            Out = Out + "<option value=\"" + Value + "\"" + Sel + ">" + Words + "</option>";
        }
        return Out + "</select></label>";
    };

    /** A stored demo choice, "" when none or blocked. */
    public static Read = (Key: string): string => {
        try {
            const Store: IDemoStore | undefined = (globalThis as unknown as { localStorage?: IDemoStore }).localStorage;
            if (Store === undefined) { return ""; }
            const V: string | null = Store.getItem(Key);
            if (V === null) { return ""; }
            return V;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: blocked storage throws; no choice is the answer.
            return "";
        }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: blocked storage = the choice is not kept; the reload shows the default. */
    private static Keep = (Key: string, Value: string): void => {
        try {
            const Store: IDemoStore | undefined = (globalThis as unknown as { localStorage?: IDemoStore }).localStorage;
            if (Store !== undefined) { Store.setItem(Key, Value); }
        } catch (e) {
            return;
        }
    };

    /** The menu's demo selects: the language set reloads the chats; a warning is told to the service, the feed shows it. */
    public static OnChange = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Target: { id?: string; value?: string } | null = Ev.target as unknown as { id?: string; value?: string } | null;
            if (Target === null || typeof Target.value !== "string") { return; }
            if (Target.id === "wac-demolang") { AppDemo.Keep(AppDemo.LangKey, Target.value); AppDemo.Reload(); return; }
            if (Target.id !== "wac-demowarn" || AppDemo.ServiceUrl.length === 0) { return; }
            AppDemo.Keep(AppDemo.WarnKey, Target.value);
            // ⚠ Through `globalThis`: this file is reached by the Bridge typecheck (no DOM lib).
            const Make: (new () => IDemoXhr) | undefined = (globalThis as unknown as { XMLHttpRequest?: new () => IDemoXhr }).XMLHttpRequest;
            if (Make === undefined) { return; }
            const Req: IDemoXhr = new Make();
            Req.open("POST", AppDemo.ServiceUrl.replace(/\/+$/, "") + "/demo/warn", true);
            Req.setRequestHeader("Content-Type", "application/json");
            Req.send(JSON.stringify({ mode: Target.value }));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDemo.OnChange");
        }
    };

    /** One delegated click listener on the document: the menu item. */
    public static Install = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: `AppMenuCheck.Listen` carries the guard and the handler.
        AppMenuCheck.Listen(AppDemo.OnClick, "AppDemo.Install");
        try {
            const Doc: { addEventListener?(T: string, F: (E: Event) => void): void } | undefined
                = (globalThis as unknown as { document?: { addEventListener?(T: string, F: (E: Event) => void): void } }).document;
            if (Doc !== undefined && typeof Doc.addEventListener === "function") { Doc.addEventListener("change", AppDemo.OnChange); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDemo.Install");
        }
    };

    public static OnClick = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Target: IDemoNode | null = Ev.target as unknown as IDemoNode | null;
            if (Target === null || typeof Target.closest !== "function") { return; }
            const Item: IDemoNode | null = (Target.closest as (S: string) => IDemoNode | null)("[" + AppDemo.Attr + "]");
            if (Item === null) { return; }
            AppDemo.Set(AppDemo.On() === false);
            AppDemo.Reload();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDemo.OnClick");
        }
    };

    /** Keeps the service address for the warning choice (demo mode has no band since 2026-10-26). */
    public static UseService = (ServiceUrl: string): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: a field write.
        if (ServiceUrl.length > 0) { AppDemo.ServiceUrl = ServiceUrl; }
    };

    /** A fresh start on the chat list: nothing of the other mode stays in memory or on screen. */
    public static Reload = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Loc: { hash: string; reload(): void } | undefined = (globalThis as unknown as { location?: { hash: string; reload(): void } }).location;
            if (Loc === undefined) { return; }
            Loc.hash = "#chats";
            Loc.reload();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDemo.Reload");
        }
    };
}
